import { computed, ref, shallowRef } from 'vue'
import type { ComputedRef, Ref, ShallowRef } from 'vue'

import {
  automaticLocalePreference,
  detectBrowserLocale,
  explicitLocalePreference,
  isLocalePreference,
  loadLocalePreference,
  persistLocalePreference,
  resolveEffectiveLocale,
} from '@/shared/i18n'
import type {
  AppLocale,
  LocalePreference,
  LocaleStorage,
} from '@/shared/i18n'
import { LOCALE_PREFERENCE_STORAGE_KEY } from '@/shared/i18n'

export interface LocalePort {
  readonly locale: ComputedRef<AppLocale>
  readonly preference: Readonly<Ref<LocalePreference>>
  readonly guildLocale: Readonly<ShallowRef<AppLocale | null>>
  readonly setExplicitLocale: (locale: AppLocale) => void
  readonly useAutomaticLocale: () => void
  readonly setActiveGuildLocale: (locale: AppLocale | null) => void
}

/**
 * Minimal event-target seam. The browser's `window` is shaped as
 * `{ addEventListener(type, handler) }`; tests pass a fake with the same
 * surface. Optional because `Node`'s `globalThis` and SSR targets lack
 * the storage event entirely.
 */
export interface LocaleEventTarget {
  readonly addEventListener?: (type: string, handler: (event: unknown) => void) => void
  readonly removeEventListener?: (type: string, handler: (event: unknown) => void) => void
}

export interface LocalePortOptions {
  /**
   * Backing store for the persisted preference. Tests pass an in-memory
   * `LocaleStorage`; production threads `window.localStorage`.
   */
  readonly storage?: LocaleStorage | null
  /**
   * `navigator.languages` snapshot used for first-boot detection.
   */
  readonly browserLanguages?: ReadonlyArray<string>
  /**
   * Target the `storage` event listener is attached to. Defaults to
   * `window` in the browser; tests pass a fake event emitter that
   * captures the handler so they can drive cross-tab sync end-to-end.
   */
  readonly eventTarget?: LocaleEventTarget | null
}

function readBrowserLanguages(): ReadonlyArray<string> {
  if (typeof navigator === 'undefined') return []
  const languages = navigator.languages.length > 0
    ? [...navigator.languages]
    : [navigator.language]
  return languages.filter((language) => language.length > 0)
}

function readBrowserStorage(): LocaleStorage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function readBrowserEventTarget(): LocaleEventTarget | null {
  return typeof window !== 'undefined' ? window : null
}

function resolveOptions(options: LocalePortOptions): {
  readonly storage: LocaleStorage | null
  readonly browserLanguages: ReadonlyArray<string>
  readonly eventTarget: LocaleEventTarget | null
} {
  return {
    storage: options.storage === undefined ? readBrowserStorage() : options.storage,
    browserLanguages: options.browserLanguages ?? readBrowserLanguages(),
    eventTarget: options.eventTarget === undefined ? readBrowserEventTarget() : options.eventTarget,
  }
}

export function createLocalePort(options: LocalePortOptions = {}): LocalePort {
  const resolved = resolveOptions(options)
  const initial = loadLocalePreference(
    { storage: resolved.storage, browserLanguages: resolved.browserLanguages },
    detectBrowserLocale,
  )
  const preference = ref<LocalePreference>(initial.preference)
  const guildLocale = shallowRef<AppLocale | null>(null)
  const locale = computed<AppLocale>(() => resolveEffectiveLocale(preference.value, guildLocale.value))

  function persist(next: LocalePreference): void {
    persistLocalePreference(resolved.storage, next)
  }

  function replacePreference(next: LocalePreference): void {
    preference.value = next
    persist(next)
  }

  function setExplicitLocale(nextLocale: AppLocale): void {
    replacePreference(explicitLocalePreference(nextLocale))
  }

  function useAutomaticLocale(): void {
    replacePreference(automaticLocalePreference(preference.value.locale))
  }

  function setActiveGuildLocale(nextLocale: AppLocale | null): void {
    guildLocale.value = nextLocale
  }

  // Cross-tab sync: another tab's `setItem` on our key surfaces here as a
  // `storage` event. We re-read the new value, validate against
  // `isLocalePreference` (so a tampered / stale JSON cannot crash the
  // resolver), and patch the preference ref. The feedback-loop guard
  // skips updates that match the current value — defence-in-depth, since
  // the browser already routes `storage` events only to *other* tabs, but
  // tests fire synthetic events in the same tab.
  function handleStorageEvent(event: { readonly key: string | null; readonly newValue: string | null }): void {
    if (event.key !== LOCALE_PREFERENCE_STORAGE_KEY) return
    if (event.newValue === null) return
    let parsed: unknown
    try {
      parsed = JSON.parse(event.newValue)
    } catch {
      return
    }
    if (!isLocalePreference(parsed)) return
    if (
      parsed.version === preference.value.version
      && parsed.mode === preference.value.mode
      && parsed.locale === preference.value.locale
    ) {
      return
    }
    preference.value = parsed
  }

  if (resolved.eventTarget !== null) {
    const target = resolved.eventTarget
    const handler = handleStorageEvent as unknown as (event: unknown) => void
    target.addEventListener?.('storage', handler)
  }

  const port: LocalePort = {
    locale,
    preference,
    guildLocale,
    setExplicitLocale,
    useAutomaticLocale,
    setActiveGuildLocale,
  }
  return port
}

let installed: LocalePort | null = null

/**
 * Installs the host singleton once. The options supplied on the first call are
 * immutable for the lifetime of the module; subsequent calls return the same
 * port regardless of their options and never attach another storage listener.
 * Tests that need isolated ports should use `createLocalePort` directly.
 */
export function installLocale(options: LocalePortOptions = {}): LocalePort {
  if (installed !== null) return installed
  installed = createLocalePort(options)
  return installed
}

export const localePort: LocalePort = new Proxy({} as LocalePort, {
  get(_target, property, receiver) {
    const port = installLocale()
    const value = Reflect.get(port as object, property, receiver)
    return typeof value === 'function' ? value.bind(port) : value
  },
}) as LocalePort